/**
 * CSE (Confined Space Evaluation) preview page.
 *
 * 2026-08-26: CSE moved into Field Reports and now renders through the
 * SAME engine as PreCon/Soft Dig/Locate — the headless-Chromium
 * renderer (utils/preview_pdf_renderer.py) prints THIS React page
 * (via MaintenanceDocument, the same generic template-backed document
 * used by MaintenancePreview.js for Grease/Maintenance Request Form)
 * instead of the old plain ReportLab template. Approve/Reject actions
 * (added Mar 2026) stay on this same page, hidden from the printed
 * output via the `no-print` class.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import FieldReportToolbar from '../components/field-report/FieldReportToolbar';
import MaintenanceDocument from '../components/maintenance/MaintenanceDocument';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CSEPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printRef = useRef();

  // Feb-2026 iter123 — approve/reject state
  const [form, setForm] = useState(null);
  const [showReject, setShowReject] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const sigCanvasRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);
  const isDrawingRef = useRef(false);

  // Server-computed authorization flag (iter125 durability fix — no
  // more client-side policy duplication). Backed by GET
  // /api/maintenance/{id}.can_approve which mirrors POST /approve's
  // real gate (CSE approver list + manager/admin/super_user).
  const canApprove = !!form?.can_approve;
  const canReject = !!form?.can_reject;

  const reload = useCallback(async () => {
    if (!token) return;
    try {
      const formResp = await axios.get(`${API}/maintenance/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let formData = formResp.data;
      // Resolve the checklist template so MaintenanceDocument can pair
      // each stored value with its label — same lookup MaintenancePreview.js
      // does for Grease / Maintenance Request Form.
      if (formData?.checklist_template_title) {
        try {
          const tplResp = await axios.get(`${API}/checklist-templates`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const tpl = (tplResp.data || []).find((t) => t.title === formData.checklist_template_title);
          if (tpl) formData = { ...formData, template: tpl };
        } catch (_tplErr) { /* render without labels rather than fail the whole preview */ }
      }
      setForm(formData);
      setError(null);
    } catch (e) {
      console.error('Failed to load CSE preview:', e);
      let detail = 'Failed to load Confined Space form';
      if (e.response?.status === 404) {
        detail = 'Confined Space form not found';
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('You must be signed in to view this form.');
      return () => {};
    }
    reload();
  }, [reload, token]);

  const handlePrint = () => window.print();

  const handleBack = () => navigate(-1);

  // ---- signature pad plumbing (same approach as CSEForm.js) --------
  const getCanvasPoint = (e) => {
    const canvas = sigCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * scaleX, y: (src.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    const { x, y } = getCanvasPoint(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawingRef.current = true;
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPoint(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasSignature) setHasSignature(true);
  };

  const stopDrawing = () => { isDrawingRef.current = false; };

  const clearSignature = () => {
    const canvas = sigCanvasRef.current;
    if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  // ---- approve / reject --------------------------------------------
  // 2026-09-05: approval now happens INLINE in the document itself
  // (matches PreCon's "sign directly in the report" pattern) instead
  // of a separate popup modal — the canvas below is rendered by
  // MaintenanceDocument via the `approvalCapture` prop, right where
  // the "Awaiting approval" placeholder used to sit.
  const submitApprove = async () => {
    if (!hasSignature) {
      toast.error('Please sign in the Manager Approval box above before approving');
      return;
    }
    setApproving(true);
    try {
      const dataUrl = sigCanvasRef.current.toDataURL('image/png');
      await axios.post(
        `${API}/maintenance/${id}/approve`,
        { signature: dataUrl },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Form approved');
      clearSignature();
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const submitReject = async () => {
    setRejecting(true);
    try {
      await axios.post(
        `${API}/maintenance/${id}/reject`,
        { reason: rejectReason || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Form rejected');
      setShowReject(false);
      setRejectReason('');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="cse-preview-loading">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading Confined Space form...</span>
        </div>
      </div>
    );
  }

  if (error || !form) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="cse-preview-error">
        <div className="text-center">
          <p className="text-slate-600 mb-4">{error || 'Confined Space form not found'}</p>
          <button
            onClick={handleBack}
            className="text-amber-600 hover:text-amber-700 font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <FieldReportToolbar
        title="Confined Space Form Preview"
        onPrint={handlePrint}
        onBack={handleBack}
      />
      <div className={`py-6 px-2 sm:px-4 flex flex-col items-center gap-3 print:p-0 print:block ${canApprove ? 'pb-32 sm:pb-24' : ''}`}>
        <div className="w-full max-w-4xl bg-white shadow-xl rounded border border-slate-200 print:shadow-none print:border-0" data-testid="cse-preview-document">
          <MaintenanceDocument
            form={form}
            printRef={printRef}
            approvalCapture={canApprove ? {
              canApprove,
              sigCanvasRef,
              onStartDrawing: startDrawing,
              onDraw: draw,
              onStopDrawing: stopDrawing,
              onClear: clearSignature,
            } : null}
          />
        </div>
      </div>

      {/* Approve / Reject action bar — visible only when the form is
          pending AND the current user is authorized to approve CSE
          (super_user OR listed in cse_restrictions.approver_ids —
          same rule as backend _cse_check). Approve now acts directly
          on the signature drawn inline in the document above (no
          popup modal) — matches PreCon's "sign in the report, then
          approve" pattern. Hidden in print output via no-print. */}
      {canApprove && (
        <div
          className="no-print fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-lg"
          data-testid="cse-preview-approval-bar"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="max-w-4xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-slate-600">
              {hasSignature
                ? 'Signature captured — approve or reject below.'
                : 'Sign in the Manager Approval box above, then approve.'}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowReject(true)}
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="cse-preview-reject-btn"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
              <Button
                onClick={submitApprove}
                disabled={!hasSignature || approving}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="cse-preview-approve-btn"
              >
                {approving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Approve
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal — reason optional (backend accepts null) */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent className="max-w-md" data-testid="cse-preview-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Confined Space Form</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Provide a reason (optional). The submitter will see this note when
              they open the form.
            </p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why is this form being rejected?"
              rows={4}
              data-testid="cse-preview-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)}>Cancel</Button>
            <Button
              onClick={submitReject}
              disabled={rejecting}
              className="bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="cse-preview-reject-confirm-btn"
            >
              {rejecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
              Confirm Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
